import { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { getApiUrl } from "../../utils/baseUrl";
import axios from "axios";
import LoaderImg from "@assets/images/loader.gif";
import { trademoTrackingEvents } from "../../core/global/trademo-tracking";
import trackingEventDictionary from "../../core/dictionary/tracking-event";
import { generateTrackingObject } from "../../core/global/global-country-functions";
import { useLocation } from "react-router-dom";
import { getUserActivePlan } from "../../helpers/profileHelper";
import { useSelector } from "react-redux";

const ReportIssueModal = ({ show, handleClose }) => {
  const [issueDescription, setIssueDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const { pathname } = useLocation();
  const dataSourceDetails = useSelector((state) => state?.shipmentDetails?.searchParameters?.dataSource);

  const MAX_FILE_SIZE = 500 * 1024;
  const MAX_CHARS = 500;

  const isSubmitDisabled = () => {
    if (!issueDescription.trim()) return true;
    return isSubmitting;
  };

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files);
    const oversizedFiles = uploadedFiles.filter((file) => file.size > MAX_FILE_SIZE);
    const invalidFiles = uploadedFiles.filter((file) => !["image/jpeg", "image/jpg", "image/png"].includes(file.type));

    if (invalidFiles.length > 0) {
      toast.error("Only JPG, JPEG and PNG files are allowed");
      return;
    }

    if (oversizedFiles.length > 0) {
      toast.error("Files must be smaller than 500KB");
      return;
    }

    if (files.length + uploadedFiles.length > 3) {
      toast.error("Maximum of 3 screenshots can be added in this section");
      return;
    }

    setFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
  };

  const removeFile = (indexToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async () => {
    trademoTrackingEvents(trackingEventDictionary.resourceCenterReportAnIssueSend, generateTrackingObject(dataSourceDetails, pathname, getUserActivePlan()));
    try {
      setIsSubmitting(true);
      setLoading(true);
      const formData = new FormData();
      formData.append("bug_description", issueDescription);
      files.forEach((file) => {
        formData.append("image", file);
      });
      await axios.post(getApiUrl("sendUserReportedBugMail"), formData);
      toast.success("Thank you for reporting the issue. Our team will review it promptly and provide an appropriate resolution soon");
      handleClose();
    } catch (error) {
      toast.error("We encountered an issue submitting your request. Please try again in a few minutes, and we’ll get back to you as soon as possible");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && (
        <div className="loader-container">
          <div className="loader-block">
            <img src={LoaderImg} alt="description of gif" />
          </div>
        </div>
      )}
      <Modal
        centered
        show={show}
        backdrop="static"
        onHide={() => {
          handleClose();
        }}>
        <Modal.Header className="tw-p-[0.5rem]">
          <Modal.Title className="!tw-text-sm !tw-font-medium">Report an Issue</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="">
            <p className="tw-text-xs tw-text-gray-700">Describe your Issue</p>
            <div className="tw-relative tw-border tw-border-gray-300 tw-h-16 tw-mt-1 tw-rounded">
              <textarea className="!tw-mt-1 tw-resize-none placeholder:tw-text-gray-400 tw-text-xs tw-border-none tw-w-full tw-h-8 tw-overflow-auto tw-px-2" rows={3} maxLength={MAX_CHARS} placeholder="Type here..." value={issueDescription} onChange={(e) => setIssueDescription(e.target.value)} />
              <div className="tw-absolute tw-bottom-2 tw-right-2 tw-text-[10px] tw-text-gray-400">{`Max ${MAX_CHARS} Characters`}</div>
            </div>
          </div>
          <div className="tw-mt-2">
            <p className="tw-text-xs tw-text-gray-700">A screenshot will help us better understand your feedback (optional)</p>
            <div className="tw-mt-1">
              <div>
                <label className={`tw-w-full ${files.length >= 3 ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`}>
                  <div className="tw-flex tw-justify-center tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600">Upload the screenshot</div>
                  <input type="file" className="tw-hidden" accept=".jpg,.jpeg,.png" multiple onChange={handleFileUpload} disabled={files.length >= 3} />
                </label>
                {files.length > 0 && (
                  <div className="tw-mt-3">
                    <p className="tw-text-xs tw-text-gray-700 tw-mt-1">Upload up to 3 files, max 500 KB each*</p>
                    <div className="tw-flex tw-flex-wrap tw-gap-5 tw-mt-1">
                      {files.map((file, index) => (
                        <div key={index} className="tw-flex tw-items-center tw-border tw-border-gray-300 tw-rounded-lg tw-p-2 tw-gap-2">
                          <span className="material-symbols-outlined">draft</span>
                          <div className="tw-flex tw-flex-col">
                            <span className="tw-text-xs tw-truncate" style={{ maxWidth: "75px" }}>
                              {file.name}
                            </span>
                            <span className="tw-text-xs tw-text-gray-500">({(file.size / 1024).toFixed(1)}kb)</span>
                          </div>
                          <button onClick={() => removeFile(index)} className="tw-ml-2 !tw-bg-transparent">
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="tw-pt-2 tw-pb-2">
          <button
            className="tw-px-6 tw-py-2 tw-cursor-pointer tw-bg-white tw-rounded-lg"
            style={{
              border: "1px solid #e7e7e7",
            }}
            onClick={handleClose}>
            Cancel
          </button>
          <button className="tw-bg-primary-purple-700 tw-rounded-lg tw-text-white tw-px-6 tw-py-2  disabled:tw-opacity-50 tw-cursor-pointer disabled:tw-cursor-not-allowed" disabled={isSubmitDisabled()} onClick={handleSubmit}>
            Send
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReportIssueModal;
